import React from 'react'
import { useSelector } from 'react-redux'
import { Maybe } from 'graphql/jsutils/Maybe'
import { Skeleton, Box, Typography } from 'transfix-ui/core'
import { ICarrierState } from 'types/carrier'
import { IStore } from 'apps/carrier/utils/reducers'
import { ShieldFilledIcon } from 'transfix-icons'
import { TBrandColor, brandColors } from 'transfix-ui'
import { useFmcsaRatingQuery } from './graphql/FMCSARating'

const getHighwaySafetyStatusColor = (rating: Maybe<string>) => {
  if (!rating || rating === 'Unrated') {
    return 'coolGray5' as TBrandColor
  }
  if (rating === 'Satisfactory') {
    return 'skyBlue6' as TBrandColor
  }
  return 'error' as TBrandColor
}

const getRating = (rating: Maybe<string>) => {
  if (rating === 'Unrated') return 'Not Rated'
  return rating || 'No Data'
}

interface IHighwaySafetyStatus {
  carrierId?: string
}

const HighwaySafetyStatus = ({ carrierId }: IHighwaySafetyStatus) => {
  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id } = carrierDetails || {}
  const carrierApplicationId = carrierId || id || ''
  const { data, loading } = useFmcsaRatingQuery({
    variables: {
      carrierApplicationId,
    },
  })
  const { highwayCarrier } = data || {}
  const { safetyRating } = highwayCarrier || {}

  const colorName = getHighwaySafetyStatusColor(safetyRating)
  const brandColor = brandColors[colorName]
  const rating = getRating(safetyRating)

  const Icon = () => {
    if (loading) return <Skeleton variant='text' width={20} height={20} />
    return <ShieldFilledIcon size='large' color={colorName} />
  }

  const Rating = () => {
    if (loading) return <Skeleton variant='text' width={95} height={19} />
    return (
      <Box color={brandColor} mr='4px'>
        <Typography variant='body2'>
          <strong>{rating}</strong>
        </Typography>
      </Box>
    )
  }

  const Suffix = () => {
    return (
      <Typography variant='body2' color='textSecondary'>
        rating on FMCSA
      </Typography>
    )
  }

  return (
    <Box display='flex' gap='4px' alignItems='center'>
      <Icon />
      <Rating />
      <Suffix />
    </Box>
  )
}

export default HighwaySafetyStatus
